import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";

export type ToastKind = "info" | "success" | "error";

export type Toast = {
  id: string;
  kind: ToastKind;
  message: string;
};

export type ErrorLogEntry = {
  id: string;
  message: string;
  ts: string;
};

const TOAST_DURATION_MS: Record<ToastKind, number> = {
  info: 4000,
  success: 4000,
  error: 6000,
};

const ERROR_LOG_CAP = 50;

type ToastCtx = {
  push: (t: { kind: ToastKind; message: string }) => void;
  errorLog: ErrorLogEntry[];
  clearErrorLog: () => void;
  removeErrorLog: (id: string) => void;
};

const Ctx = createContext<ToastCtx | null>(null);

export function useToast() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useToast must be used within <ToastProvider>");
  const ref = useRef<{ push: ToastCtx["push"] } | null>(null);
  if (!ref.current || ref.current.push !== ctx.push) {
    ref.current = { push: ctx.push };
  }
  return ref.current;
}

export function useErrorLog() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useErrorLog must be used within <ToastProvider>");
  return { entries: ctx.errorLog, clear: ctx.clearErrorLog, remove: ctx.removeErrorLog };
}

export function ToastProvider(props: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [errorLog, setErrorLog] = useState<ErrorLogEntry[]>([]);

  const push = useCallback((t: { kind: ToastKind; message: string }) => {
    const id = crypto.randomUUID();
    setToasts((prev) => [...prev, { id, ...t }]);
    if (t.kind === "error") {
      const entry: ErrorLogEntry = { id, message: t.message, ts: new Date().toISOString() };
      setErrorLog((prev) => {
        const next = [entry, ...prev];
        return next.length > ERROR_LOG_CAP ? next.slice(0, ERROR_LOG_CAP) : next;
      });
    }
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, TOAST_DURATION_MS[t.kind]);
  }, []);

  const clearErrorLog = useCallback(() => setErrorLog([]), []);

  const removeErrorLog = useCallback((id: string) => {
    setErrorLog((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const value = useMemo(
    () => ({ push, errorLog, clearErrorLog, removeErrorLog }),
    [push, errorLog, clearErrorLog, removeErrorLog],
  );

  return (
    <Ctx.Provider value={value}>
      {props.children}
      <div className="toastStack" aria-live="polite" aria-relevant="additions removals">
        {toasts.map((t) => (
          <div key={t.id} className={`toast toast-${t.kind}`}>
            {t.message}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}
